/**
 * Dietitian add-on keys that unlock existing Admin UI.
 */
export const ADDON_KEYS = {
  recipe: "recipe",
  ai_features: "ai_features",
  user_management: "user_management",
  partners: "partners",
  tickets: "tickets",
  white_labeling: "white_labeling",
  payments: "payments",
  booking_appointment: "booking_appointment",
  audio_video_call: "audio_video_call",
  hire_pa: "hire_pa",
  hire_dietitian: "hire_dietitian",
};

/** Dependent add-on → required add-ons that must be active before purchase. */
export const ADDON_PURCHASE_DEPENDENCIES = {
  [ADDON_KEYS.recipe]: [ADDON_KEYS.ai_features],
  [ADDON_KEYS.audio_video_call]: [ADDON_KEYS.booking_appointment],
};

const ADDON_LABELS = {
  [ADDON_KEYS.booking_appointment]: "Booking Appointment",
  [ADDON_KEYS.audio_video_call]: "Audio / Video Call",
  [ADDON_KEYS.payments]: "Payments Management",
  [ADDON_KEYS.user_management]: "User Management",
  [ADDON_KEYS.tickets]: "Tickets",
  [ADDON_KEYS.white_labeling]: "White Labeling",
  [ADDON_KEYS.recipe]: "Recipe",
  [ADDON_KEYS.ai_features]: "AI Features",
  [ADDON_KEYS.partners]: "Partners",
  [ADDON_KEYS.hire_pa]: "Personal Assistant",
  [ADDON_KEYS.hire_dietitian]: "Personal Dietitians",
};

export function addonLabel(key) {
  return (
    ADDON_LABELS[key] ||
    String(key || "")
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase())
  );
}

export function addonPurchaseDependencies(addonKey) {
  return ADDON_PURCHASE_DEPENDENCIES[String(addonKey || "")] || [];
}

/** Returns missing prerequisite keys for an addon given the catalog list. */
export function missingAddonPurchaseDependencies(addonKey, addons = []) {
  const owned = new Set(
    (addons || []).filter((a) => a?.purchased).map((a) => String(a.key)),
  );
  return addonPurchaseDependencies(addonKey).filter((dep) => !owned.has(dep));
}

export function addonDependencyBlockedMessage(addonKey, missingDeps) {
  const first = (missingDeps || [])[0];
  const label = addonLabel(first);
  return `To use this feature, you must first activate the ${label} add-on.`;
}

/** Sidebar/route path → required add-on key (omit = always allowed). */
export const ROUTE_ADDON_KEYS = {
  "/admin/tickets": ADDON_KEYS.tickets,
  "/admin/recipes": ADDON_KEYS.recipe,
  "/admin/users": ADDON_KEYS.user_management,
  "/admin/groups": ADDON_KEYS.user_management,
  "/admin/user-management": ADDON_KEYS.user_management,
  "/admin/partners": ADDON_KEYS.partners,
  "/admin/partners/withdrawals": ADDON_KEYS.partners,
  "/admin/white-labeling": ADDON_KEYS.white_labeling,
  "/admin/payment-history": ADDON_KEYS.payments,
  "/admin/payments": ADDON_KEYS.payments,
  "/admin/appointments": ADDON_KEYS.booking_appointment,
  "/admin/personal-assistant": ADDON_KEYS.hire_pa,
  "/admin/hire-dietitians": ADDON_KEYS.hire_dietitian,
};

/** Extra add-ons that must ALL be present for a route (in addition to ROUTE_ADDON_KEYS). */
export const ROUTE_ADDON_ALL_KEYS = {
  "/admin/recipes": [ADDON_KEYS.recipe, ADDON_KEYS.ai_features],
};

export function companyHasAddon(config, key) {
  if (!key) return true;
  const active = Array.isArray(config?.active_addons)
    ? config.active_addons
    : [];
  const row = active.find(
    (a) => a?.key && String(a.key) === String(key),
  );
  if (row) {
    const status = String(row.status || "active");
    if (status === "inactive") return false;
    if (status === "cancelled") {
      if (!row.expires_at) return true;
      return new Date(row.expires_at).getTime() > Date.now();
    }
    return true;
  }
  const features = Array.isArray(config?.features) ? config.features : [];
  return features.map(String).includes(String(key));
}

export function companyHasAllAddons(config, keys) {
  return (keys || []).every((k) => companyHasAddon(config, k));
}

/** Where an activated add-on should open from the sidebar submenu. */
export const ADDON_NAV_ITEMS = [
  {
    key: ADDON_KEYS.booking_appointment,
    path: "/admin/appointments",
    permKey: "customers",
  },
  {
    key: ADDON_KEYS.payments,
    path: "/admin/payments",
    permKey: "payment_history",
  },
  {
    key: ADDON_KEYS.user_management,
    path: "/admin/user-management",
    permAny: ["users", "groups"],
  },
  {
    key: ADDON_KEYS.tickets,
    path: "/admin/tickets",
    permKey: "tickets",
  },
  {
    key: ADDON_KEYS.white_labeling,
    path: "/admin/white-labeling",
    permKey: "settings",
  },
  {
    key: ADDON_KEYS.recipe,
    path: "/admin/recipes",
    permKey: "customers",
    requireKeys: [ADDON_KEYS.recipe, ADDON_KEYS.ai_features],
  },
  {
    key: ADDON_KEYS.partners,
    path: "/admin/partners",
    permAny: ["partners", "withdrawals"],
  },
  {
    key: ADDON_KEYS.hire_pa,
    path: "/admin/personal-assistant",
    permKey: "addons",
  },
  {
    key: ADDON_KEYS.hire_dietitian,
    path: "/admin/hire-dietitians",
    permKey: "addons",
  },
];

function addonCatalogPath(key) {
  return `/admin/addons?addon=${encodeURIComponent(key)}`;
}

/** Purchased capabilities with no dedicated Admin screen on web or mobile. */
const ADDONS_WITHOUT_NAV_UI = new Set([
  ADDON_KEYS.audio_video_call,
  ADDON_KEYS.ai_features,
]);

export function listActivatedAddonNav({
  config,
  canSee,
  hasAddon,
  aiEnabled = true,
} = {}) {
  const rows = Array.isArray(config?.active_addons) ? config.active_addons : [];
  const nameByKey = new Map(
    rows
      .filter((a) => a?.key)
      .map((a) => [
        String(a.key),
        ADDON_LABELS[a.key] || a.name || a.key,
      ]),
  );
  const seen = new Set();
  const items = [];

  const push = (key, spec = {}) => {
    const k = String(key);
    if (!k || seen.has(k) || ADDONS_WITHOUT_NAV_UI.has(k) || !hasAddon?.(k)) return;
    if ((spec.requireKeys || []).some((req) => !hasAddon(req))) {
      items.push({
        key: k,
        label: nameByKey.get(k) || ADDON_LABELS[k] || k,
        path: addonCatalogPath(k),
      });
      seen.add(k);
      return;
    }
    if (spec.permKey && canSee && !canSee(spec.permKey)) return;
    if (
      Array.isArray(spec.permAny) &&
      spec.permAny.length &&
      canSee &&
      !spec.permAny.some((p) => canSee(p))
    ) {
      return;
    }
    const path = spec.path || addonCatalogPath(k);
    items.push({
      key: k,
      label: nameByKey.get(k) || ADDON_LABELS[k] || k,
      path,
    });
    seen.add(k);
  };

  ADDON_NAV_ITEMS.forEach((spec) => push(spec.key, spec));
  rows.forEach((row) => {
    if (row?.key) push(row.key, {});
  });
  (Array.isArray(config?.features) ? config.features : []).forEach((key) => {
    if (key) push(key, {});
  });
  return items;
}
